import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import { keyframes, useTheme } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';

// 🔹 Scroll animation (adjusted for mobile)
const scrollOnce = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
`;

// Pulse animation
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.15); opacity: 0.9; }
  100% { transform: scale(1); opacity: 1; }
`;

const TopNav = ({ colorInvert = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 🔹 Shorter duration for faster scroll start on mobile
  const scrollDuration = isMobile ? '42s' : '35s';

  return (
    <Box
      display="flex"
      alignItems="center"
      width="100%"
      sx={{
        backgroundColor: colorInvert ? 'transparent' : '#fff',
        borderBottom: '1px solid #ddd',
        overflow: 'hidden',
        px: { xs: 1, sm: 2 },
        py: { xs: 0.3, sm: 0.5 },
        height: { xs: 40, sm: 48 },
      }}
    >
      {/* 🔹 Scrolling message */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          position: 'relative',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            animation: `${scrollOnce} ${scrollDuration} linear infinite`,
            animationDelay: isMobile ? '-8s' : '0s', // 🔹 Start early on mobile
            '&:hover': { animationPlayState: 'paused' },
          }}
        >
          <Box
            component="img"
            src="https://npsypr-website.blr1.cdn.digitaloceanspaces.com/static-assets/videos/home.gif"
            alt="New"
            sx={{
              width: { xs: 20, sm: 30, md: 40 },
              height: { xs: 20, sm: 30, md: 40 },
              verticalAlign: 'middle',
              animation: `${pulse} 2s infinite`,
            }}
          />

          <Typography
            component="div"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              fontWeight: 600,
              color: colorInvert ? 'common.white' : 'blue',
              fontSize: { xs: 11, sm: 13, md: 15 },
            }}
          >
             <Link href="/admission-criteria" underline="hover" color="inherit">
            Admissions Open for 2027-28
            </Link>
              <Box component="span">||</Box>{' '}
            <Link
              href="/class10-results-2026/"
              underline="hover"
              color="inherit"
            >
              SSE Class 10 Results 2025-26
            </Link>
            <Box component="span">||</Box>{' '}
            <Link
              href="/class12-results-2026/"
              underline="hover"
              color="inherit"
            >
              SSCE Class 12 Results 2025-26
            </Link>
            <Box component="span">||</Box>{' '}
            <Link href="/jee-results-2026/" underline="hover" color="inherit">
              JEE MAIN TOPPERS 2025-26
            </Link>
            <Box component="span">||</Box>{' '}
            <Link
              href="/jee-advanced-results-2026/"
              underline="hover"
              color="inherit"
            >
              JEE ADVANCED ACHIEVERS 2025-26
            </Link>
            ||{' '}
            
            <Link
              href="/scholastic#senior-school-learning-programme"
              underline="hover"
              color="inherit"
            >
              Class 11 INSPIRE (NEET/JEE /CET)
            </Link>
            ||{' '}
            <Link href="/#activities" underline="hover" color="inherit">
              HIGHLIGHTS OF 2025
            </Link>
            ||{' '}
            <Link href="/careers" underline="hover" color="inherit">
              Careers
            </Link>
          
           
           
            {/* Add icons + virtual tour in scroller for mobile */}
            {isMobile && (
              <>
                ||{' '}
                <Link href="/virtual-tour" underline="none">
                  <Box
                    px={1}
                    py={0.3}
                    borderRadius={1}
                    bgcolor="primary.main"
                    sx={{ color: 'common.white', fontWeight: 600 }}
                  >
                    Virtual Tour
                  </Box>
                </Link>

                ||{' '}
                <Link
                  href="https://www.facebook.com/p/National-Public-School-Yeshwanthpur-100084410342747/"
                  target="_blank"
                  rel="noopener"
                  sx={{ color: '#1877F2' }}
                >
                  <FacebookIcon sx={{ animation: `${pulse} 2s infinite` }} />
                </Link>
                <Link
                  href="https://www.instagram.com/nps_ypr/"
                  target="_blank"
                  rel="noopener"
                  sx={{ color: '#E4405F' }}
                >
                  <InstagramIcon sx={{ animation: `${pulse} 2s infinite` }} />
                </Link>
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {/* Desktop: right section with virtual tour + icons */}
      {!isMobile && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            flexShrink: 0,
            ml: 2,
            gap: 1.5,
          }}
        >
          <Link
            underline="none"
            href="/virtual-tour"
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              px={1}
              py={0.3}
              borderRadius={1}
              bgcolor={'primary.main'}
              sx={{
                '&:hover': { opacity: 0.9 },
                whiteSpace: 'nowrap',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: 'common.white', fontWeight: 600, lineHeight: 1 }}
              >
                Virtual Tour
              </Typography>
            </Box>
          </Link>

          <Link
            href="https://www.facebook.com/p/National-Public-School-Yeshwanthpur-100084410342747/"
            target="_blank"
            rel="noopener"
            sx={{
              color: '#1877F2',
              animation: `${pulse} 2s infinite`,
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.3) rotate(5deg)',
                color: '#1256c2',
              },
            }}
          >
            <FacebookIcon fontSize="small" />
          </Link>

          <Link
            href="https://www.instagram.com/nps_ypr/"
            target="_blank"
            rel="noopener"
            sx={{
              color: '#E4405F',
              animation: `${pulse} 2s infinite`,
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.3) rotate(-5deg)',
                color: '#C13584',
              },
            }}
          >
            <InstagramIcon fontSize="small" />
          </Link>
        </Box>
      )}
    </Box>
  );
};

TopNav.propTypes = {
  colorInvert: PropTypes.bool,
};

export default TopNav;
